import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Paper,
  Chip,
  Rating,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';


const groupItemsByCategory = (items = []) => {
  return items.reduce((acc, item) => {
    const key = item?.category?.name || 'بدون فئة';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

const isPublicApprovedDrink = (drink) =>
  Number(drink?.is_approved) === 1 &&
  Number(drink?.is_public) === 1 &&
  Number(drink?.status) === 1;

const ClintOrderMenuSection = ({
  menuTab,
  setMenuTab,
  activeProducts,
  activeDrinks,
  activeMyDrinks = [],
  onQuickAddProduct,
  onQuickAddDrink,
  getItemImage,
  toNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const groupedProducts = groupItemsByCategory(activeProducts);
  const visibleActiveDrinks = useMemo(
    () => (Array.isArray(activeDrinks) ? activeDrinks.filter(isPublicApprovedDrink) : []),
    [activeDrinks]
  );
  const groupedMyDrinks = groupItemsByCategory(activeMyDrinks);
  const [materialsDialogOpen, setMaterialsDialogOpen] = useState(false);
  const [selectedDrinkForMaterials, setSelectedDrinkForMaterials] = useState(null);
  const [selectedCategoryByTab, setSelectedCategoryByTab] = useState({});

  const selectedDrinkMaterials = useMemo(() => {
    if (!selectedDrinkForMaterials?.materials || !Array.isArray(selectedDrinkForMaterials.materials)) {
      return [];
    }
    return selectedDrinkForMaterials.materials;
  }, [selectedDrinkForMaterials]);

  const handleOpenMaterialsDialog = (event, drink) => {
    event.stopPropagation();
    setSelectedDrinkForMaterials(drink);
    setMaterialsDialogOpen(true);
  };

  const handleCloseMaterialsDialog = () => {
    setMaterialsDialogOpen(false);
    setSelectedDrinkForMaterials(null);
  };

  const handleSelectCategory = (tabKey, categoryName) => {
    setSelectedCategoryByTab((prev) => ({ ...prev, [tabKey]: categoryName }));
  };

  const handleBackToCategories = (tabKey) => {
    setSelectedCategoryByTab((prev) => ({ ...prev, [tabKey]: null }));
  };

  const getVisibleCategoryEntries = (tabKey, groupedItems) => {
    const selectedCategory = selectedCategoryByTab[tabKey];
    if (!selectedCategory) return Object.entries(groupedItems);
    return [[selectedCategory, groupedItems[selectedCategory] || []]];
  };

  const renderCategorySelection = (tabKey, entries, color = 'primary', Icon = Inventory2Icon) => (
    <Grid container spacing={1.25}>
      {entries.map(([categoryName, categoryItems]) => (
        <Grid item xs={12} sm={6} key={categoryName}>
          <Paper
            variant="outlined"
            onClick={() => handleSelectCategory(tabKey, categoryName)}
            sx={{
              p: 1.5,
              minHeight: 86,
              cursor: 'pointer',
              borderRadius: 2.2,
              borderColor: alpha(theme.palette[color].main, 0.22),
              background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: `${color}.main`,
                boxShadow: isMobile ? 'none' : `0 8px 20px ${alpha(theme.palette[color].main, 0.15)}`,
                transform: isMobile ? 'none' : 'translateY(-2px)',
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: `${color}.main`,
                  backgroundColor: alpha(theme.palette[color].main, 0.12),
                  flexShrink: 0,
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                  {categoryName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {categoryItems.length} عنصر
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderSelectedCategoryHeader = (tabKey, categoryName, color = 'primary') => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
      <Button
        size="small"
        variant="outlined"
        color={color}
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={() => handleBackToCategories(tabKey)}
        sx={{ borderRadius: 1.5, fontWeight: 800 }}
      >
        التصنيفات
      </Button>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, color: `${color}.main` }} noWrap>
        {categoryName}
      </Typography>
    </Stack>
  );

  return (
    <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 4 } }}>
      <Card
        sx={{
          height: '100%',
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 1.5,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <RestaurantMenuIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  المنيو
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                  اختر العنصر المناسب وأضفه مباشرة للسلة
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Tabs
            value={menuTab}
            onChange={(e, value) => setMenuTab(value)}
            variant="fullWidth"
            sx={{
              mb: 1.5,
              minHeight: isMobile ? 40 : 44,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: isMobile ? 11 : 12,
                minHeight: isMobile ? 40 : 44,
                px: isMobile ? 0.5 : 1,
              },
            }}
          >
            <Tab value="products" label={`المنتجات (${activeProducts.length})`} />
            <Tab value="drinks" label={`اصناف تم صنعها بواسطة العملاء (${visibleActiveDrinks.length})`} />
            <Tab value="my-drinks" label={`مشروباتي (${activeMyDrinks.length})`} />
          </Tabs>

          <Box
            sx={{
              maxHeight: { xs: 500, md: 540 },
              overflowY: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 7 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.primary.main, 0.25),
                borderRadius: 8,
              },
            }}
          >
            {menuTab === 'products' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!selectedCategoryByTab.products ? (
                  renderCategorySelection('products', Object.entries(groupedProducts), 'primary', Inventory2Icon)
                ) : (
                  <>
                    {renderSelectedCategoryHeader('products', selectedCategoryByTab.products, 'primary')}
                    {getVisibleCategoryEntries('products', groupedProducts).map(([categoryName, categoryItems]) => (
                  <Box key={categoryName}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        mb: 1,
                        px: 1,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                      }}
                    >
                      <Inventory2Icon sx={{ fontSize: 16 }} />
                      {categoryName}
                    </Typography>
                    <Grid container spacing={1.25}>
                      {categoryItems.map((product) => {
                        return (
                          <Grid item xs={12} key={product.id}>
                            <Paper
                              variant="outlined"
                              onClick={() => onQuickAddProduct(product)}
                              sx={{
                                p: 1.25,
                                cursor: 'pointer',
                                borderRadius: 2.2,
                                borderColor: alpha(theme.palette.primary.main, 0.2),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  boxShadow: isMobile ? 'none' : `0 8px 20px ${alpha(theme.palette.primary.main, 0.16)}`,
                                  transform: isMobile ? 'none' : 'translateY(-2px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1.25 }}>
                                <Box
                                  component="img"
                                  src={getItemImage(product)}
                                  alt={product.name || 'product'}
                                  sx={{
                                    width: 68,
                                    height: 68,
                                    borderRadius: 1.5,
                                    objectFit: 'cover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    flexShrink: 0,
                                  }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {product.name || '-'}
                                  </Typography>
                                  {product.name_en && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {product.name_en}
                                    </Typography>
                                  )}
                                  {product.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                    >
                                      {product.description}
                                    </Typography>
                                  )}

                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.8 }}>
                                    <Chip size="small" color="primary" label={`${toNumber(product.price).toFixed(2)} ج.م`} />
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                    ))}
                  </>
                )}
              </Box>
            )}

            {menuTab === 'drinks' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Grid container spacing={1.25}>
                      {visibleActiveDrinks.map((drink) => {
                        const materialsCount = Array.isArray(drink.materials) ? drink.materials.length : 0;
                        const ratingCount = Array.isArray(drink.ratings) ? drink.ratings.length : 0;
                        const averageRating =
                          ratingCount > 0
                            ? drink.ratings.reduce((sum, rating) => sum + Number(rating?.rate || 0), 0) / ratingCount
                            : 0;

                        return (
                          <Grid item xs={12} key={drink.id}>
                            <Paper
                              variant="outlined"
                              onClick={() => onQuickAddDrink(drink)}
                              sx={{
                                p: 1.25,
                                cursor: 'pointer',
                                borderRadius: 2.2,
                                borderColor: alpha(theme.palette.secondary.main, 0.25),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'secondary.main',
                                  boxShadow: isMobile ? 'none' : `0 8px 20px ${alpha(theme.palette.secondary.main, 0.16)}`,
                                  transform: isMobile ? 'none' : 'translateY(-2px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1.25 }}>
                                <Box
                                  component="img"
                                  src={getItemImage(drink)}
                                  alt={drink.drink_name || drink.name || 'drink'}
                                  sx={{
                                    width: 68,
                                    height: 68,
                                    borderRadius: 1.5,
                                    objectFit: 'cover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    flexShrink: 0,
                                  }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {drink.drink_name || drink.name || '-'}
                                  </Typography>
                                  {drink.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                    >
                                      {drink.description}
                                    </Typography>
                                  )}

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.7 }}>
                                    <Rating value={averageRating} precision={0.5} size="small" readOnly />
                                    <Typography variant="caption" color="text.secondary">
                                      ({ratingCount})
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.8 }}>
                                    <Chip size="small" color="secondary" label={`${toNumber(drink.price).toFixed(2)} ج.م`} />
                                    {drink.temperature && <Chip size="small" label={drink.temperature} variant="outlined" />}
                                    {drink.taste && <Chip size="small" label={drink.taste} variant="outlined" />}
                                    <Chip size="small" label={`المكونات: ${materialsCount}`} variant="outlined" />
                                    <Chip size="small" label={`التقييمات: ${ratingCount}`} variant="outlined" />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      onClick={(event) => handleOpenMaterialsDialog(event, drink)}
                                      sx={{ minWidth: 0, px: 1.1, py: 0.2, borderRadius: 1.5 }}
                                    >
                                      عرض
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
              </Box>
            )}

            {menuTab === 'my-drinks' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!selectedCategoryByTab['my-drinks'] ? (
                  renderCategorySelection('my-drinks', Object.entries(groupedMyDrinks), 'success', LocalCafeIcon)
                ) : (
                  <>
                    {renderSelectedCategoryHeader('my-drinks', selectedCategoryByTab['my-drinks'], 'success')}
                    {getVisibleCategoryEntries('my-drinks', groupedMyDrinks).map(([categoryName, categoryItems]) => (
                  <Box key={categoryName}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        mb: 1,
                        px: 1,
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                      }}
                    >
                      <LocalCafeIcon sx={{ fontSize: 16 }} />
                      {categoryName}
                    </Typography>
                    <Grid container spacing={1.25}>
                      {categoryItems.map((drink) => {
                        const materialsCount = Array.isArray(drink.materials) ? drink.materials.length : 0;
                        const ratingCount = Array.isArray(drink.ratings) ? drink.ratings.length : 0;
                        const averageRating =
                          ratingCount > 0
                            ? drink.ratings.reduce((sum, rating) => sum + Number(rating?.rate || 0), 0) / ratingCount
                            : 0;

                        return (
                          <Grid item xs={12} key={drink.id}>
                            <Paper
                              variant="outlined"
                              onClick={() => onQuickAddDrink(drink)}
                              sx={{
                                p: 1.25,
                                cursor: 'pointer',
                                borderRadius: 2.2,
                                borderColor: alpha(theme.palette.success.main, 0.24),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'success.main',
                                  boxShadow: isMobile ? 'none' : `0 8px 20px ${alpha(theme.palette.success.main, 0.16)}`,
                                  transform: isMobile ? 'none' : 'translateY(-2px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 1.25 }}>
                                <Box
                                  component="img"
                                  src={getItemImage(drink)}
                                  alt={drink.drink_name || drink.name || 'my-drink'}
                                  sx={{
                                    width: 68,
                                    height: 68,
                                    borderRadius: 1.5,
                                    objectFit: 'cover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    flexShrink: 0,
                                  }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {drink.drink_name || drink.name || '-'}
                                  </Typography>

                                  {drink.approval_notes && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                    >
                                      {drink.approval_notes}
                                    </Typography>
                                  )}

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.7 }}>
                                    <Rating value={averageRating} precision={0.5} size="small" readOnly />
                                    <Typography variant="caption" color="text.secondary">
                                      ({ratingCount})
                                    </Typography>
                                  </Box>

                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.8 }}>
                                    <Chip size="small" color="success" label={`${toNumber(drink.price).toFixed(2)} ج.م`} />
                                    {drink.temperature && <Chip size="small" label={drink.temperature} variant="outlined" />}
                                    {drink.taste && <Chip size="small" label={drink.taste} variant="outlined" />}
                                    <Chip size="small" label={`المكونات: ${materialsCount}`} variant="outlined" />
                                    <Chip size="small" label={`التقييمات: ${ratingCount}`} variant="outlined" />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={(event) => handleOpenMaterialsDialog(event, drink)}
                                      sx={{ minWidth: 0, px: 1.1, py: 0.2, borderRadius: 1.5 }}
                                    >
                                      عرض
                                    </Button>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                    ))}
                  </>
                )}

                {activeMyDrinks.length === 0 && (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      لا توجد مشروبات قمت بإنشائها حتى الآن.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={materialsDialogOpen}
        onClose={handleCloseMaterialsDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>
          مكونات: {selectedDrinkForMaterials?.drink_name || selectedDrinkForMaterials?.name || '-'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDrinkMaterials.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              لا توجد مكونات متاحة لهذا المشروب.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedDrinkMaterials.map((item) => {
                const materialName = item?.material?.name || item?.material?.name_en || `#${item?.material_id || '-'}`;
                const quantity = item?.quantity ?? '-';
                const unit =
                  item?.material?.measurment_unit?.abbreviation ||
                  item?.material?.measurment_unit?.name ||
                  '';

                return (
                  <Paper key={item.id || `${item.material_id}_${quantity}`} variant="outlined" sx={{ p: 1.2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {materialName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {quantity} {unit}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMaterialsDialog}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ClintOrderMenuSection;
